function perfNow() { return globalThis.performance?.now ? performance.now() : Date.now(); }

function f32ToF16Bits(v) {
  const f32 = new Float32Array(1);
  const u32 = new Uint32Array(f32.buffer);
  f32[0] = v;
  const x = u32[0];
  const sign = (x >>> 16) & 0x8000;
  let mant = x & 0x7fffff;
  let exp = (x >>> 23) & 0xff;
  if (exp === 0xff) return sign | (mant ? 0x7e00 : 0x7c00);
  exp = exp - 127 + 15;
  if (exp >= 0x1f) return sign | 0x7c00;
  if (exp <= 0) {
    if (exp < -10) return sign;
    mant = (mant | 0x800000) >>> (1 - exp);
    return sign | ((mant + 0x1000) >>> 13);
  }
  return sign | (exp << 10) | ((mant + 0x1000) >>> 13);
}

function packF16(src, n) {
  const out = new Uint16Array(n * 64);
  for (let i = 0; i < out.length; i++) out[i] = f32ToF16Bits(src[i]);
  return out;
}

function align4(n) { return (n + 3) & ~3; }

export class GpuMnnMatcher {
  constructor({ preferF16 = true } = {}) {
    this.preferF16 = preferF16;
    this.readyPromise = null;
    this.device = null;
    this.useF16 = false;
    this.pipelines = null;
    this.bufferCache = new WeakMap();
  }

  async init() {
    if (this.readyPromise) return this.readyPromise;
    this.readyPromise = this._init();
    return this.readyPromise;
  }

  async _init() {
    if (!globalThis.navigator?.gpu) throw new Error("WebGPU unavailable for MNN");
    const adapter = await navigator.gpu.requestAdapter();
    if (!adapter) throw new Error("WebGPU adapter unavailable for MNN");
    this.useF16 = !!this.preferF16 && adapter.features?.has?.("shader-f16");
    const requiredFeatures = this.useF16 ? ["shader-f16"] : [];
    this.device = await adapter.requestDevice({ requiredFeatures });
    this.pipelines = this._createPipelines();
    return this;
  }

  _createPipelines() {
    const dty = this.useF16 ? "f16" : "f32";
    const enable = this.useF16 ? "enable f16;\n" : "";
    const common = `${enable}
struct Params { n0: u32, n1: u32, _pad0: u32, _pad1: u32 };
@group(0) @binding(0) var<storage, read> d0: array<${dty}>;
@group(0) @binding(1) var<storage, read> d1: array<${dty}>;
@group(0) @binding(2) var<storage, read_write> sim: array<f32>;
@group(0) @binding(3) var<storage, read_write> best01: array<i32>;
@group(0) @binding(4) var<storage, read_write> best01v: array<f32>;
@group(0) @binding(5) var<storage, read_write> best10: array<i32>;
@group(0) @binding(6) var<storage, read_write> best10v: array<f32>;
@group(0) @binding(7) var<uniform> p: Params;
`;
    const simCode = `${common}
@compute @workgroup_size(16, 16)
fn main(@builtin(global_invocation_id) gid: vec3<u32>) {
  let j = gid.x;
  let i = gid.y;
  if (i >= p.n0 || j >= p.n1) { return; }
  var s: f32 = 0.0;
  let o0 = i * 64u;
  let o1 = j * 64u;
  for (var c: u32 = 0u; c < 64u; c = c + 1u) {
    s = s + f32(d0[o0 + c]) * f32(d1[o1 + c]);
  }
  sim[i * p.n1 + j] = s;
}`;
    const rowCode = `${common}
@compute @workgroup_size(64)
fn main(@builtin(global_invocation_id) gid: vec3<u32>) {
  let i = gid.x;
  if (i >= p.n0) { return; }
  var best: f32 = -1.0e20;
  var idx: i32 = -1;
  let row = i * p.n1;
  for (var j: u32 = 0u; j < p.n1; j = j + 1u) {
    let v = sim[row + j];
    if (v > best) { best = v; idx = i32(j); }
  }
  best01[i] = idx;
  best01v[i] = best;
}`;
    const colCode = `${common}
@compute @workgroup_size(64)
fn main(@builtin(global_invocation_id) gid: vec3<u32>) {
  let j = gid.x;
  if (j >= p.n1) { return; }
  var best: f32 = -1.0e20;
  var idx: i32 = -1;
  for (var i: u32 = 0u; i < p.n0; i = i + 1u) {
    let v = sim[i * p.n1 + j];
    if (v > best) { best = v; idx = i32(i); }
  }
  best10[j] = idx;
  best10v[j] = best;
}`;
    const bgl = this.device.createBindGroupLayout({ entries: [
      { binding: 0, visibility: GPUShaderStage.COMPUTE, buffer: { type: "read-only-storage" } },
      { binding: 1, visibility: GPUShaderStage.COMPUTE, buffer: { type: "read-only-storage" } },
      { binding: 2, visibility: GPUShaderStage.COMPUTE, buffer: { type: "storage" } },
      { binding: 3, visibility: GPUShaderStage.COMPUTE, buffer: { type: "storage" } },
      { binding: 4, visibility: GPUShaderStage.COMPUTE, buffer: { type: "storage" } },
      { binding: 5, visibility: GPUShaderStage.COMPUTE, buffer: { type: "storage" } },
      { binding: 6, visibility: GPUShaderStage.COMPUTE, buffer: { type: "storage" } },
      { binding: 7, visibility: GPUShaderStage.COMPUTE, buffer: { type: "uniform" } },
    ] });
    const layout = this.device.createPipelineLayout({ bindGroupLayouts: [bgl] });
    const mk = (code) => this.device.createComputePipeline({ layout, compute: { module: this.device.createShaderModule({ code }), entryPoint: "main" } });
    return { sim: mk(simCode), row: mk(rowCode), col: mk(colCode), bgl };
  }

  _gpuBufferFor(descs, n) {
    const cached = this.bufferCache.get(descs);
    const fmt = this.useF16 ? "f16" : "f32";
    if (cached && cached.n === n && cached.fmt === fmt) return cached.buffer;
    const data = this.useF16 ? packF16(descs, n) : descs.subarray(0, n * 64);
    const size = align4(data.byteLength);
    const buffer = this.device.createBuffer({ size, usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST });
    this.device.queue.writeBuffer(buffer, 0, data);
    this.bufferCache.set(descs, { n, fmt, buffer });
    return buffer;
  }

  _makeBuffer(size, usage) {
    return this.device.createBuffer({ size: Math.max(4, align4(size)), usage });
  }

  release(descs) {
    const cached = this.bufferCache.get(descs);
    if (!cached) return;
    try { cached.buffer.destroy(); } catch {}
    this.bufferCache.delete(descs);
  }

  async match(d0, n0, d1, n1, minCossim = 0.82) {
    await this.init();
    const tTotal = perfNow();
    let t = perfNow();
    const b0 = this._gpuBufferFor(d0, n0);
    const b1 = this._gpuBufferFor(d1, n1);
    const uploadMs = perfNow() - t;
    const simBytes = n0 * n1 * 4;
    const best01Bytes = n0 * 4;
    const best10Bytes = n1 * 4;
    const sim = this._makeBuffer(simBytes, GPUBufferUsage.STORAGE);
    const best01 = this._makeBuffer(best01Bytes, GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC);
    const best01v = this._makeBuffer(best01Bytes, GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC);
    const best10 = this._makeBuffer(best10Bytes, GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC);
    const best10v = this._makeBuffer(best10Bytes, GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_SRC);
    const params = this._makeBuffer(16, GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST);
    this.device.queue.writeBuffer(params, 0, new Uint32Array([n0, n1, 0, 0]));
    const bindGroup = this.device.createBindGroup({
      layout: this.pipelines.bgl,
      entries: [
        { binding: 0, resource: { buffer: b0 } },
        { binding: 1, resource: { buffer: b1 } },
        { binding: 2, resource: { buffer: sim } },
        { binding: 3, resource: { buffer: best01 } },
        { binding: 4, resource: { buffer: best01v } },
        { binding: 5, resource: { buffer: best10 } },
        { binding: 6, resource: { buffer: best10v } },
        { binding: 7, resource: { buffer: params } },
      ],
    });
    const read01 = this._makeBuffer(best01Bytes, GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ);
    const read01v = this._makeBuffer(best01Bytes, GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ);
    const read10 = this._makeBuffer(best10Bytes, GPUBufferUsage.COPY_DST | GPUBufferUsage.MAP_READ);
    t = perfNow();
    const enc = this.device.createCommandEncoder();
    {
      const pass = enc.beginComputePass();
      pass.setPipeline(this.pipelines.sim);
      pass.setBindGroup(0, bindGroup);
      pass.dispatchWorkgroups(Math.ceil(n1 / 16), Math.ceil(n0 / 16));
      pass.end();
    }
    {
      const pass = enc.beginComputePass();
      pass.setPipeline(this.pipelines.row);
      pass.setBindGroup(0, bindGroup);
      pass.dispatchWorkgroups(Math.ceil(n0 / 64));
      pass.end();
    }
    {
      const pass = enc.beginComputePass();
      pass.setPipeline(this.pipelines.col);
      pass.setBindGroup(0, bindGroup);
      pass.dispatchWorkgroups(Math.ceil(n1 / 64));
      pass.end();
    }
    enc.copyBufferToBuffer(best01, 0, read01, 0, best01Bytes);
    enc.copyBufferToBuffer(best01v, 0, read01v, 0, best01Bytes);
    enc.copyBufferToBuffer(best10, 0, read10, 0, best10Bytes);
    this.device.queue.submit([enc.finish()]);
    await Promise.all([read01.mapAsync(GPUMapMode.READ), read01v.mapAsync(GPUMapMode.READ), read10.mapAsync(GPUMapMode.READ)]);
    const gpuMs = perfNow() - t;
    t = perfNow();
    const best01Arr = new Int32Array(read01.getMappedRange().slice(0));
    const best01vArr = new Float32Array(read01v.getMappedRange().slice(0));
    const best10Arr = new Int32Array(read10.getMappedRange().slice(0));
    read01.unmap(); read01v.unmap(); read10.unmap();
    const readMs = perfNow() - t;
    const idx0 = [], idx1 = [];
    let maxScore = -Infinity;
    for (let i = 0; i < n0; i++) {
      const j = best01Arr[i];
      const score = best01vArr[i];
      if (score > maxScore) maxScore = score;
      if (j >= 0 && best10Arr[j] === i && score > minCossim) {
        idx0.push(i); idx1.push(j);
      }
    }
    sim.destroy(); best01.destroy(); best01v.destroy(); best10.destroy(); best10v.destroy(); params.destroy(); read01.destroy(); read01v.destroy(); read10.destroy();
    return { idx0, idx1, timing: { gpu: true, f16: this.useF16, upload_ms: uploadMs, gpu_ms: gpuMs, read_ms: readMs, total_ms: perfNow() - tTotal, max_score: maxScore, first_idx: best01Arr[0], first_score: best01vArr[0] } };
  }
}
